import { TAccount } from '@helpers/Accounts/accounts.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { fetchGetAccounts, GetAccountsResponse } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TLoanOverviewData = {
    durationInMonths: number;
    interestRate: number;
    principal: number;
    interest: number;
    outstandingBalance: number;
    loanStatus: TAccount['loan_status'];
    nextRepaymentAmount: number;
    nextRepaymentDate: string | null;
    productType: TAccount['product_type'];
    estimatedTimeToRepay: number | undefined;
    sweepFraction: number | undefined;
};

type TAccountsResponse = GetAccountsResponse['data']['accounts'][0];

export const getMaxMonthlyInterestRate = (account: TAccountsResponse) => {
    if (account.scheduled_payments) {
        const rates =
            account.scheduled_payments!.summary!.rate_structures![0].rates;
        const maxRate = _.maxBy(rates, 'max_principal');

        return maxRate!.rate!;
    }

    return account.interest_rates![0].rate!;
};

const getNextRepaymentAmount = (account: TAccountsResponse) => {
    if (account.scheduled_payments)
        return account.scheduled_payments.schedule![0].total;

    return 0;
};

const getNextRepaymentDate = (account: TAccountsResponse) => {
    if (account.scheduled_payments)
        return account.scheduled_payments.schedule![0].date;

    return null;
};

export const useGetLoanOverviewDetails = (
    accountId: string
): TLoanOverviewData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });
    if (!account) {
        throw new Error();
    }

    return {
        durationInMonths: getDurationInMonths(account.duration!),
        interestRate: getMaxMonthlyInterestRate(account),
        principal: account.pending_balance!.principal!,
        interest: account.pending_balance!.interest!,
        outstandingBalance: account.pending_balance!.total!,
        loanStatus: account.loan_status,
        nextRepaymentAmount: getNextRepaymentAmount(account),
        nextRepaymentDate: getNextRepaymentDate(account),
        productType: account.product_type,
        estimatedTimeToRepay:
            account.revenue_based_loan_details
                ?.estimated_time_to_repay_in_months,
        sweepFraction:
            account.revenue_based_loan_details?.revenue_sweep_fraction
    };
};
